/**
 * Every record of one book, read at one moment (part 24a, deep-fix r2: table R and principle 9): the book record, its
 * entries and conflict copies, the text of every chapter they name, every snapshot of those chapters and the text of each.
 * Both adapters read through {@link gatherBook}, which asks for each record as soon as the one naming it is in, so the
 * IndexedDB adapter can issue every request inside one readonly transaction: a write by this tab or another (a split, a
 * restore, a thinning) lands wholly before the read or wholly after it.
 *
 * A record the store's reader refuses is kept with its raw value, and what is stored under its key is still read when the
 * key is an ID: a refused entry's text and snapshots, a refused snapshot's text (table R).
 *
 * The book's images (part 16c, table I) are every record of the `images` and `imageBytes` stores under the book's key
 * range, read in the same transaction, whatever the text names: a record and its bytes are each kept by the ID part of
 * their key, readable or refused.
 *
 * The book's notes (part 19e, V5-1, V5-2) are every record the `notes` store's book index holds under the book, read in
 * the same transaction, readable or refused, whatever chapter they name; a record still in the stores the notes were moved
 * from (`bookNotes`, `comments`) is read as refused (MG14, V5-3).
 *
 * The book's reading links (part 38d, V6-1) are every record the `shareLinks` store's book index holds under the book, in
 * the same transaction, readable or refused.
 *
 * The book's shares (part 38b, table BU) are every record the `shares` store's book index holds under the book, in the
 * same transaction, readable or refused.
 */
import { IMAGE_BYTES, IMAGES, isId, readImage, readImageBytes, readNote, readRecord, readShare, readShareLink, STORE_NAMES, StoreRecordError } from './records.ts';
import type { BookRecord, ChapterEntry, ChapterText, ConflictCopy, ImageBytesRecord, ImageRecord, NoteRecord, RecordOf, ShareLinkRecord, ShareRecord, Snapshot, SnapshotText, StoreName } from './records.ts';

/**
 * A store a book's records are read from: every record store, the book notes included (part 19d), and the images and their
 * bytes (part 16c); never the pending imports' (part 24c), which hold no words.
 */
export type BookStoreName = Exclude<StoreName, 'imports' | 'templates'> | typeof IMAGES | typeof IMAGE_BYTES;

/** Every store a book is read from, in a fixed order: the record stores' order, then the images and their bytes. */
export const BOOK_STORE_NAMES: readonly BookStoreName[] = Object.freeze([...STORE_NAMES.filter((name): name is Exclude<StoreName, 'imports' | 'templates'> => name !== 'imports' && name !== 'templates'), IMAGES, IMAGE_BYTES]);

/** A stored record its reader refused (R4), with the value as stored and, when an index found it, the value it was found by. */
export interface RefusedRecord {
  readonly store: BookStoreName;
  /** The record's key; for an image store, the ID part of its `[bookId, id]` key (part 16c). */
  readonly key: unknown;
  /** The value as stored: anything another build or a damaged store left. */
  readonly raw: unknown;
  /** The index value it was found under (the book's ID for entries, copies and images, the chapter's for snapshots), or null. */
  readonly under: string | null;
  readonly error: unknown;
}

/** One book's records, read at one moment (table R). */
export interface BookContents {
  /** The book record; undefined when there is none or it was refused. */
  readonly book: BookRecord | undefined;
  /** The book's entries, in key order. */
  readonly entries: readonly ChapterEntry[];
  /** The book's conflict copies, in key order. */
  readonly copies: readonly ConflictCopy[];
  /** The text of each chapter named (by a readable entry, a refused entry's ID key, a copy), by chapter ID. */
  readonly texts: ReadonlyMap<string, ChapterText>;
  /** The readable snapshots of each chapter named, by chapter ID, each list in key order. */
  readonly snapshots: ReadonlyMap<string, readonly Snapshot[]>;
  /** The text of each snapshot (readable, or refused with an ID key), by snapshot ID. */
  readonly snapshotTexts: ReadonlyMap<string, SnapshotText>;
  /** The book's readable image records, by the ID their key holds, in key order (part 16c, table I). */
  readonly images: ReadonlyMap<string, ImageRecord>;
  /** The book's readable image bytes, by the ID their key holds, in key order (table I). */
  readonly imageBytes: ReadonlyMap<string, ImageBytesRecord>;
  /** The book's readable note records, tombstones included, by ID, in key order (part 19e, V5-1). */
  readonly notes: ReadonlyMap<string, NoteRecord>;
  /** The book's readable reading-link records, by ID, in key order (part 38d, V6-1). */
  readonly shareLinks: ReadonlyMap<string, ShareLinkRecord>;
  /** The book's readable share records, by ID, in key order (part 38b, table BU). */
  readonly shares: ReadonlyMap<string, ShareRecord>;
  /** Every record refused on the way, in the order met. */
  readonly refused: readonly RefusedRecord[];
}

/** The reads an adapter offers {@link gatherBook}: each calls back with what is stored, raw, inside the same read. */
export interface BookReads {
  /** The value stored under `key`, or undefined. */
  get(store: StoreName, key: string, then: (raw: unknown) => void): void;
  /** Every `[key, value]` the store's index on `field` holds under `value`, in key order. */
  where(store: 'entries' | 'conflictCopies' | 'snapshots' | 'bookNotes' | 'comments' | 'notes' | 'shareLinks' | 'shares', field: 'bookId' | 'chapterId', value: string, then: (rows: readonly (readonly [unknown, unknown])[]) => void): void;
  /** Every `[id, value]` the image store holds under a `[bookId, id]` key, in key order: `id` is the key's second part. */
  images(store: typeof IMAGES | typeof IMAGE_BYTES, bookId: string, then: (rows: readonly (readonly [unknown, unknown])[]) => void): void;
}

/**
 * I13: reads an image store's `raw` under the ID `id` of book `bookId`: the store's reader, and a record of that book and
 * ID (IndexedDB makes the key from those fields; a record planted elsewhere may disagree).
 */
function readImageRow<T extends { readonly bookId: string; readonly id: string }>(read: (raw: unknown) => T, bookId: string, id: unknown, raw: unknown): T {
  const record = read(raw);
  if (record.bookId !== bookId || record.id !== id) throw new StoreRecordError('id', 'must be the key it is stored under');
  return record;
}

/**
 * B3, B‴2, V5-2: reads a note's `raw` under the key `key` with its store's reader `read`, and a record of that ID (IndexedDB
 * makes the key from it; a record planted elsewhere may disagree, and two keys holding one ID would name it twice in the
 * bundle).
 */
function readKeyedRow<T extends { readonly id: string }>(read: (raw: unknown) => T, key: unknown, raw: unknown): T {
  const record = read(raw);
  if (record.id !== key) throw new StoreRecordError('id', 'must be the key it is stored under');
  return record;
}

/**
 * R1–R8, I1–I14, B1–B7, BU: asks `reads` for every record of book `bookId`, each as soon as what names it is in; returns what gives
 * the contents once every callback has run (the adapter's read has ended).
 */
export function gatherBook(bookId: string, reads: BookReads): () => BookContents {
  const entries: ChapterEntry[] = [];
  const copies: ConflictCopy[] = [];
  const texts = new Map<string, ChapterText>();
  const snapshots = new Map<string, Snapshot[]>();
  const snapshotTexts = new Map<string, SnapshotText>();
  const images = new Map<string, ImageRecord>();
  const imageBytes = new Map<string, ImageBytesRecord>();
  const notes = new Map<string, NoteRecord>();
  const shareLinks = new Map<string, ShareLinkRecord>();
  const shares = new Map<string, ShareRecord>();
  const refused: RefusedRecord[] = [];
  const named = new Set<string>();
  let book: BookRecord | undefined;

  /** The record `raw` read with `read`, or undefined after keeping it as refused from `store`. */
  const take = <T>(store: BookStoreName, key: unknown, raw: unknown, under: string | null, read: (value: unknown) => T): T | undefined => {
    try {
      return read(raw);
    } catch (error) {
      refused.push({ store, key, raw, under, error });
      return undefined;
    }
  };
  /** The record `raw` read with `store`'s reader, or undefined after keeping it as refused. */
  const accept = <S extends Exclude<StoreName, 'imports' | 'templates'>>(store: S, key: unknown, raw: unknown, under: string | null): RecordOf<S> | undefined =>
    take(store, key, raw, under, (value) => readRecord(store, value));
  /** R6: a snapshot's text, under the snapshot's ID. */
  const snapshotText = (id: string): void => {
    reads.get('snapshotTexts', id, (raw) => {
      if (raw === undefined) return;
      const text = accept('snapshotTexts', id, raw, null);
      if (text !== undefined) snapshotTexts.set(id, text);
    });
  };
  /** R2, R3, R5: a chapter named for the first time: its text, its snapshots and theirs. */
  const chapter = (id: string): void => {
    if (named.has(id)) return;
    named.add(id);
    reads.get('chapters', id, (raw) => {
      if (raw === undefined) return;
      const text = accept('chapters', id, raw, null);
      if (text !== undefined) texts.set(id, text);
    });
    reads.where('snapshots', 'chapterId', id, (rows) => {
      const list: Snapshot[] = [];
      snapshots.set(id, list);
      for (const [key, raw] of rows) {
        const snapshot = accept('snapshots', key, raw, id);
        if (snapshot !== undefined) list.push(snapshot);
        // R5: a refused snapshot's text is still stored under its key, its ID.
        const textId = snapshot?.id ?? (isId(key) ? key : null);
        if (textId !== null) snapshotText(textId);
      }
    });
  };

  reads.get('books', bookId, (raw) => {
    book = raw === undefined ? undefined : accept('books', bookId, raw, null);
  });
  reads.where('entries', 'bookId', bookId, (rows) => {
    for (const [key, raw] of rows) {
      const entry = accept('entries', key, raw, bookId);
      if (entry !== undefined) entries.push(entry);
      // R2: a refused entry's key is its chapter's ID, which its text and snapshots are stored under.
      const chapterId = entry?.id ?? (isId(key) ? key : null);
      if (chapterId !== null) chapter(chapterId);
    }
    reads.where('conflictCopies', 'bookId', bookId, (copyRows) => {
      for (const [key, raw] of copyRows) {
        const copy = accept('conflictCopies', key, raw, bookId);
        if (copy === undefined) continue;
        copies.push(copy);
        chapter(copy.chapterId);
      }
    });
  });

  // I1–I14: the book's images and their bytes, by its key range, whatever names them.
  reads.images(IMAGES, bookId, (rows) => {
    for (const [id, raw] of rows) {
      const image = take(IMAGES, id, raw, bookId, (value) => readImageRow(readImage, bookId, id, value));
      if (image !== undefined) images.set(image.id, image);
    }
  });
  reads.images(IMAGE_BYTES, bookId, (rows) => {
    for (const [id, raw] of rows) {
      const bytes = take(IMAGE_BYTES, id, raw, bookId, (value) => readImageRow(readImageBytes, bookId, id, value));
      if (bytes !== undefined) imageBytes.set(bytes.id, bytes);
    }
  });

  // V5-1, V5-2 (part 19e): the book's notes, by its index, readable or refused; a note's key names nothing else.
  reads.where('notes', 'bookId', bookId, (rows) => {
    for (const [key, raw] of rows) {
      const note = take('notes', key, raw, bookId, (value) => readKeyedRow(readNote, key, value));
      if (note !== undefined) notes.set(note.id, note);
    }
  });

  // V6-1 (part 38d): the book's reading links, by its index, readable or refused; a link's key names nothing else.
  reads.where('shareLinks', 'bookId', bookId, (rows) => {
    for (const [key, raw] of rows) {
      const link = take('shareLinks', key, raw, bookId, (value) => readKeyedRow(readShareLink, key, value));
      if (link !== undefined) shareLinks.set(link.id, link);
    }
  });

  // BU (part 38b): the book's shares, by its index, readable or refused; a share's key names nothing else.
  reads.where('shares', 'bookId', bookId, (rows) => {
    for (const [key, raw] of rows) {
      const share = take('shares', key, raw, bookId, (value) => readKeyedRow(readShare, key, value));
      if (share !== undefined) shares.set(share.id, share);
    }
  });

  // MG14, V5-3: what is left in the stores the notes moved from, refused by their readers; its key names nothing else.
  for (const store of ['bookNotes', 'comments'] as const) {
    reads.where(store, 'bookId', bookId, (rows) => {
      for (const [key, raw] of rows) accept(store, key, raw, bookId);
    });
  }

  return () => ({ book, entries, copies, texts, snapshots, snapshotTexts, images, imageBytes, notes, shareLinks, shares, refused });
}
